import useTracks from '@/compositions/useTracks';
import useStreams from '@/compositions/useStreams';
import { ref, watch } from '@vue/composition-api';
import useSocket from '@/compositions/useSocket';
import useRoom from '@/compositions/useRoom';
import useJanusPublisher from '@/compositions/useJanusPublisher';
import usePublishers from '@/compositions/usePublishers';
import useErrors from './useErrors';

const captured = ref(false);
const capturedTrack = ref(null);
const eventHandlerSet = ref(false);
const { networkError } = useErrors();

export default function useCaptureScreen() {
  const { replaceTrack, videoTrack, setVideoTrack } = useTracks();
  const { room } = useRoom();
  const { janusSession } = useJanusPublisher();
  const { updatePublisher } = usePublishers();
  const {
    storeCameraStream, restoreCameraStream, setMyStream,
    myCapture,
  } = useStreams();

  const { registerMessage, sendEvent } = useSocket();

  async function stopCapture() {
    if (!captured.value || !videoTrack.value) {
      return;
    }
    await replaceTrack(videoTrack.value);
    capturedTrack.value.stop();
    captured.value = false;
    myCapture.value = false;
    restoreCameraStream();
  }

  function getScreenStream() {
    return navigator.mediaDevices.getDisplayMedia({
      video: {
        width: 1980,
        height: 1024,
      },
      audio: true,
    });
  }

  function emitEvent(state) {
    sendEvent('action:share', {
      roomId: room.value,
      state,
      janusRoomMemberId: janusSession.value.janusRoomMemberId,
    });
  }

  watch(networkError, async () => {
    if (!networkError.value) {
      return;
    }
    if (captured.value) {
      await stopCapture();
    }
  });

  async function startCapture() {
    if (captured.value) {
      await stopCapture();
      emitEvent(false);
      return;
    }
    try {
      const captureStream = await getScreenStream();
      const video = captureStream.getVideoTracks()[0];
      video.onended = () => {
        stopCapture();
        emitEvent(false);
      };

      capturedTrack.value = video;

      storeCameraStream();
      setVideoTrack(await replaceTrack(video));
      captured.value = true;
      myCapture.value = true;
      setMyStream(captureStream);
      emitEvent(true);
    } catch (err) {
      console.error(`Error: ${err}`);
    }
  }

  if (!eventHandlerSet.value) {
    registerMessage('room:share', ({ janusRoomMemberId, state }) => {
      updatePublisher(janusRoomMemberId, { share: state });

      if (janusSession.value.janusRoomMemberId === janusRoomMemberId || !myCapture.value || !state) {
        return;
      }
      stopCapture();
      emitEvent(false);
    });
  }

  eventHandlerSet.value = true;

  return {
    startCapture,
    captured,
  };
}
